import { getLatestPlayableApprovedAsset } from '@/helpers/media-processing-insight.helper';
import type { MediaDto } from '@/types/dtos/media/media.dto';

export type PresentationStepPosition = {
  slideNumber: number;
  /** Highest original slide number in the sequence (hidden slides are skipped, not renumbered). */
  slideTotal: number;
  step: number;
  stepCount: number;
};

/**
 * In presentation step mode the sequence index counts animation steps, not slides.
 * `frameSlideNumbers[i]` is the original slide number of frame i + 1.
 */
export function resolvePresentationStepPosition(
  frameIndex: number | undefined,
  frameSlideNumbers: number[] | undefined,
): PresentationStepPosition | undefined {
  if (!frameSlideNumbers?.length || frameIndex == null || frameIndex < 1 || frameIndex > frameSlideNumbers.length) {
    return undefined;
  }

  const slideNumber = frameSlideNumbers[frameIndex - 1];
  const firstFrame = frameSlideNumbers.indexOf(slideNumber);
  const stepCount = frameSlideNumbers.filter((value) => value === slideNumber).length;

  return {
    slideNumber,
    slideTotal: Math.max(...frameSlideNumbers),
    step: frameIndex - firstFrame,
    stepCount,
  };
}

/** First frame (1-based) of the requested slide, or of the next playable slide when that one is hidden. */
export function resolveFirstFrameForSlide(
  slideNumber: number,
  frameSlideNumbers: number[] | undefined,
): number | undefined {
  if (!frameSlideNumbers?.length) {
    return undefined;
  }

  const index = frameSlideNumbers.findIndex((value) => value >= slideNumber);
  if (index >= 0) {
    return index + 1;
  }
  return frameSlideNumbers.indexOf(frameSlideNumbers[frameSlideNumbers.length - 1]) + 1;
}

/** Slide map from `imageSequence` layer data (`frameSlideNumbers` JSON). */
export function parseFrameSlideNumbers(raw: string | undefined): number[] | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.length > 0 && parsed.every((value) => Number.isInteger(value) && value > 0)
      ? (parsed as number[])
      : undefined;
  } catch {
    return undefined;
  }
}

/** Step-frame slide map from the effective approved playout asset, if that asset has one. */
export function resolveMediaFrameSlideNumbers(media: Pick<MediaDto, 'assets'> | undefined): number[] | undefined {
  const map = getLatestPlayableApprovedAsset(media?.assets ?? [])?.slidePreviews?.frameSlideNumbers;
  return map?.length ? map : undefined;
}
